import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  canSendStateEvent,
  clearPinned as matrixClearPinned,
  getRoomEvent,
  pinEvent as matrixPinEvent,
  subscribePinned,
  subscribeTimeline,
  unpinEvent as matrixUnpinEvent,
  type Client,
  type TimelineEvent,
  type Unsubscribe,
} from "~/lib/matrix";
import {
  UNDECRYPTABLE_LABEL,
  UNSUPPORTED_LABEL,
  replacementBody,
  replacementBodyOverride,
} from "~/lib/message-utils";

const UNAVAILABLE_RETRY_MS = 30_000;

export type PinnedMessageStatus = "loaded" | "loading" | "unavailable";

export interface PinnedMessagePreview {
  eventId: string;
  sender: string | null;
  body: string;
  status: PinnedMessageStatus;
}

export interface UsePinnedResult {
  eventIds: string[];
  pins: PinnedMessagePreview[];
  canManage: boolean;
  isPinned: (eventId: string) => boolean;
  pin: (eventId: string) => Promise<void>;
  unpin: (eventId: string) => Promise<void>;
  clear: () => Promise<void>;
}

export function buildPinnedPreviews(
  pinnedEventIds: readonly string[],
  timelineEvents: readonly TimelineEvent[],
  fetchedEvents: ReadonlyMap<string, TimelineEvent | null>,
): PinnedMessagePreview[] {
  const eventById = new Map<string, TimelineEvent>();
  for (const event of timelineEvents) eventById.set(event.id, event);

  return pinnedEventIds.map((eventId) => {
    const event = eventById.get(eventId) ?? fetchedEvents.get(eventId) ?? null;
    if (event) return previewForLoadedEvent(eventId, event);
    if (fetchedEvents.has(eventId)) {
      return { eventId, sender: null, body: "Message unavailable", status: "unavailable" };
    }
    return { eventId, sender: null, body: "Loading message", status: "loading" };
  });
}

export function usePinned(
  client: Client | null,
  roomId: string | null,
  userId: string | null,
  timelineEvents?: TimelineEvent[],
): UsePinnedResult {
  const [eventIds, setEventIds] = useState<string[]>([]);
  const [subscribedEvents, setSubscribedEvents] = useState<TimelineEvent[]>([]);
  const [fetchedEvents, setFetchedEvents] = useState<Map<string, TimelineEvent | null>>(new Map());
  const [retryTick, setRetryTick] = useState(0);
  const fetchedEventsRef = useRef(fetchedEvents);
  const fetchingEventIdsRef = useRef<Set<string>>(new Set());
  const requestScopeRef = useRef(0);
  const unavailableRetryTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    fetchedEventsRef.current = fetchedEvents;
  }, [fetchedEvents]);

  useEffect(() => {
    setEventIds([]);
    if (!client || !roomId) return;
    const unsub: Unsubscribe = subscribePinned(client, roomId, (next) => {
      setEventIds((current) => (sameStringArray(current, next) ? current : next));
    });
    return () => unsub();
  }, [client, roomId]);

  useEffect(() => {
    if (timelineEvents) return;
    setSubscribedEvents([]);
    if (!client || !roomId) return;
    const unsub = subscribeTimeline(client, roomId, setSubscribedEvents);
    return () => unsub();
  }, [client, roomId, timelineEvents]);

  useEffect(() => {
    requestScopeRef.current += 1;
    fetchingEventIdsRef.current.clear();
    for (const timer of unavailableRetryTimersRef.current) clearTimeout(timer);
    unavailableRetryTimersRef.current.clear();
    setFetchedEvents(new Map());
    setRetryTick(0);
  }, [client, roomId]);

  const events = timelineEvents ?? subscribedEvents;
  const timelineEventIds = useMemo(() => new Set(events.map((event) => event.id)), [events]);

  useEffect(() => {
    if (!client || !roomId) return;
    const activeClient = client;
    const activeRoomId = roomId;
    const requestScope = requestScopeRef.current;
    const missing = eventIds.filter(
      (eventId) => !timelineEventIds.has(eventId) && !fetchedEventsRef.current.has(eventId),
    );
    const missingToFetch = missing.filter((eventId) => !fetchingEventIdsRef.current.has(eventId));
    if (missingToFetch.length === 0) return;

    for (const eventId of missingToFetch) fetchingEventIdsRef.current.add(eventId);

    async function fetchMissing() {
      const entries = await Promise.all(
        missingToFetch.map(async (eventId) => {
          try {
            return [eventId, await getRoomEvent(activeClient, activeRoomId, eventId)] as const;
          } catch {
            return [eventId, null] as const;
          }
        }),
      );
      for (const [eventId] of entries) fetchingEventIdsRef.current.delete(eventId);
      if (requestScope !== requestScopeRef.current) return;

      setFetchedEvents((current) => {
        const next = new Map(current);
        for (const [eventId, event] of entries) next.set(eventId, event);
        return next;
      });
      const unavailable = entries.filter(([, event]) => event === null).map(([eventId]) => eventId);
      if (unavailable.length > 0) {
        const timer = setTimeout(() => {
          unavailableRetryTimersRef.current.delete(timer);
          setFetchedEvents((current) => {
            const next = new Map(current);
            for (const eventId of unavailable) {
              if (next.get(eventId) === null) next.delete(eventId);
            }
            return next;
          });
          setRetryTick((tick) => tick + 1);
        }, UNAVAILABLE_RETRY_MS);
        unavailableRetryTimersRef.current.add(timer);
      }
    }

    void fetchMissing();
  }, [client, roomId, eventIds, timelineEventIds, retryTick]);

  useEffect(() => {
    const timers = unavailableRetryTimersRef.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const pins = useMemo(
    () => buildPinnedPreviews(eventIds, events, fetchedEvents),
    [eventIds, events, fetchedEvents],
  );

  const canManage =
    !!client &&
    !!roomId &&
    !!userId &&
    canSendStateEvent(client, roomId, userId, "m.room.pinned_events");

  const isPinned = useCallback((eventId: string) => eventIds.includes(eventId), [eventIds]);

  const pin = useCallback(
    async (eventId: string) => {
      if (!client || !roomId) return;
      await matrixPinEvent(client, roomId, eventId);
    },
    [client, roomId],
  );

  const unpin = useCallback(
    async (eventId: string) => {
      if (!client || !roomId) return;
      await matrixUnpinEvent(client, roomId, eventId);
    },
    [client, roomId],
  );

  const clear = useCallback(async () => {
    if (!client || !roomId) return;
    await matrixClearPinned(client, roomId);
  }, [client, roomId]);

  return { eventIds, pins, canManage, isPinned, pin, unpin, clear };
}

function previewForLoadedEvent(eventId: string, event: TimelineEvent): PinnedMessagePreview {
  return {
    eventId,
    sender: event.sender.displayName ?? null,
    body: previewForEvent(event),
    status: "loaded",
  };
}

function previewForEvent(event: TimelineEvent): string {
  switch (event.content.type) {
    case "m.text":
    case "m.emote":
    case "m.notice":
      return replacementBody(event.content);
    case "m.image":
      return replacementBody(event.content) || "Image";
    case "m.video":
      return replacementBody(event.content) || "Video";
    case "m.file":
      return replacementBody(event.content) || "File";
    case "m.audio":
      return replacementBody(event.content) || "Audio";
    case "m.location":
      return (
        replacementBodyOverride(event.content) ||
        event.content.description ||
        event.content.body ||
        "Location"
      );
    case "m.reaction":
      return event.content.key ? `Reaction ${event.content.key}` : "Reaction";
    case "m.room.redaction":
      return event.content.reason || "Redaction";
    case "m.room.encrypted":
      return UNDECRYPTABLE_LABEL;
    case "m.redacted":
      return "Message removed";
    case "m.room.member":
      return `${event.sender.displayName} changed membership`;
    case "m.room.name":
      return `Room renamed to ${event.content.name}`;
    case "m.room.topic":
      return `Topic changed to ${event.content.topic}`;
    case "m.room.power_levels":
      return "Power levels changed";
    case "m.room.tombstone":
      return event.content.body || "Room tombstoned";
    case "m.room.canonical_alias":
      return event.content.alias
        ? `Room alias changed to ${event.content.alias}`
        : "Room aliases changed";
    case "m.room.history_visibility":
      return event.content.historyVisibility
        ? `History visibility changed to ${event.content.historyVisibility}`
        : "History visibility changed";
    case "m.room.join_rules":
      return event.content.joinRule
        ? `Join rules changed to ${event.content.joinRule}`
        : "Join rules changed";
    case "m.room.guest_access":
      return event.content.guestAccess
        ? `Guest access changed to ${event.content.guestAccess}`
        : "Guest access changed";
    case "m.room.avatar":
      return "Room avatar changed";
    case "m.room.create":
      return event.content.roomType === "m.space" ? "Space created" : "Room created";
    case "m.space.child":
      return "Space children changed";
    case "m.space.parent":
      return "Space parents changed";
    case "com.nurion.channel_type":
      return "Channel type changed";
    case "com.nurion.archived":
      return "Archive state changed";
    case "m.state":
      return event.content.eventType;
    case "m.undecryptable":
      return UNDECRYPTABLE_LABEL;
    case "m.unsupported":
      return UNSUPPORTED_LABEL;
  }
}

function sameStringArray(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}
